import { PERSONALIZATION_STORAGE_KEYS } from '../constants';
import type { IPersonalizationStorageAdapter } from '../types';

/**
 * Minimal async key-value interface (compatible with @react-native-async-storage/async-storage).
 * Injected via constructor to avoid React Native dependency in packages/utils.
 */
export interface IAsyncKeyValueStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/**
 * Write-through cache adapter: synchronous IPersonalizationStorageAdapter interface
 * backed by an async key-value store (e.g. AsyncStorage).
 *
 * Call `hydrate()` once before use to pre-load values from the async store.
 * After hydration, all reads are synchronous from the in-memory cache,
 * and all writes persist to the async store in fire-and-forget fashion.
 *
 * All values (actorId, sessionId, sessionTimestamp) are persisted to the async store.
 */
export class AsyncStoragePersonalizationStorageAdapter implements IPersonalizationStorageAdapter {
  private readonly store: IAsyncKeyValueStore;
  private cachedActorId: string | null = null;
  private sessionId: string | null = null;
  private sessionTimestamp: number | null = null;

  constructor(store: IAsyncKeyValueStore) {
    this.store = store;
  }

  async hydrate(): Promise<void> {
    try {
      const [actorId, sessionId, sessionTs] = await Promise.all([
        this.store.getItem(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID),
        this.store.getItem(PERSONALIZATION_STORAGE_KEYS.SESSION_ID),
        this.store.getItem(PERSONALIZATION_STORAGE_KEYS.SESSION_TS),
      ]);
      this.cachedActorId = actorId;
      this.sessionId = sessionId;
      this.sessionTimestamp = sessionTs ? Number(sessionTs) : null;
    } catch {
      // silent fail — tracking must never break the app
    }
  }

  getActorId(): string | null {
    return this.cachedActorId;
  }

  setActorId(actorId: string): void {
    this.cachedActorId = actorId;
    this.store.setItem(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID, actorId).catch(() => {
      // silent fail
    });
  }

  removeActorId(): void {
    this.cachedActorId = null;
    this.store.removeItem(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID).catch(() => {
      // silent fail
    });
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.store.setItem(PERSONALIZATION_STORAGE_KEYS.SESSION_ID, sessionId).catch(() => {
      // silent fail
    });
  }

  removeSessionId(): void {
    this.sessionId = null;
    this.store.removeItem(PERSONALIZATION_STORAGE_KEYS.SESSION_ID).catch(() => {
      // silent fail
    });
  }

  getSessionTimestamp(): number | null {
    return this.sessionTimestamp;
  }

  setSessionTimestamp(timestamp: number): void {
    this.sessionTimestamp = timestamp;
    this.store.setItem(PERSONALIZATION_STORAGE_KEYS.SESSION_TS, String(timestamp)).catch(() => {
      // silent fail
    });
  }

  removeSessionTimestamp(): void {
    this.sessionTimestamp = null;
    this.store.removeItem(PERSONALIZATION_STORAGE_KEYS.SESSION_TS).catch(() => {
      // silent fail
    });
  }
}
